// Shared renderer that draws the bull with a character seated ASTRIDE it
// (legs straddling, body upright, the bull's back hiding the lower legs) —
// used by both the Rodeo Level and the Bull Riding bonus for a consistent,
// properly-seated rider instead of a figure standing on top.
import { ready } from "./assets";

// Measure a sprite's opaque vertical extent (as fractions of its height) ONCE and
// cache it, so characters with different amounts of empty padding all seat at the
// same height on the bull instead of some sitting high and others sinking in.
function contentBounds(img) {
  if (img._cb) return img._cb;
  try {
    const H = 120;
    const W = Math.max(1, Math.round(img.naturalWidth * (H / img.naturalHeight)));
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const g = c.getContext("2d");
    g.drawImage(img, 0, 0, W, H);
    const data = g.getImageData(0, 0, W, H).data;
    let top = H, bottom = -1;
    for (let y = 0; y < H; y++) {
      let has = false;
      for (let x = 0; x < W; x++) { if (data[(y * W + x) * 4 + 3] > 24) { has = true; break; } }
      if (has) { if (y < top) top = y; bottom = y; }
    }
    if (bottom < top) { img._cb = { top: 0, bottom: 1 }; }
    else img._cb = { top: top / H, bottom: (bottom + 1) / H };
  } catch (e) {
    img._cb = { top: 0, bottom: 1 };
  }
  return img._cb;
}

export function drawBullRider(ctx, { assets, char, cx, cy, bullW = 250, tilt = 0, duck = 0, riderScale = 1, noShadow = false }) {
  const bull = assets.rodeo_bull;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(tilt);

  // ground shadow (skippable so the Rodeo Level can pin it to the lane floor)
  if (!noShadow) {
    ctx.fillStyle = "rgba(0,0,0,0.28)";
    ctx.beginPath(); ctx.ellipse(0, 2, bullW * 0.46, 20, 0, 0, Math.PI * 2); ctx.fill();
  }

  let bullH = bullW * 0.53;
  if (ready(bull)) {
    bullH = bull.naturalHeight * (bullW / bull.naturalWidth);
    ctx.drawImage(bull, -bullW / 2, -bullH, bullW, bullH);
  } else {
    ctx.fillStyle = "#4a3425";
    ctx.beginPath(); ctx.ellipse(0, -bullH * 0.5, bullW * 0.48, bullH * 0.5, 0, 0, Math.PI * 2); ctx.fill();
  }

  // Seat line: top of the bull's back where the saddle blanket sits.
  const saddleY = -bullH * 0.62;
  const baseId = char.spriteId || char.id;
  const ride = assets[`${baseId}_ride`];

  // Preferred: a purpose-drawn seated riding sprite. To make EVERY character sit
  // cleanly (regardless of the source pose) we draw the upper body normally, then
  // show only a narrow strip of the lap at the seat, then redraw the bull's body
  // over everything below — so legs tuck behind the bull and no rough/dangling
  // cut-out legs ever spill into the bull's transparent gaps or below its belly.
  if (ready(ride)) {
    // Normalise by the sprite's actual opaque content so every character seats
    // the same, regardless of padding. The character's hip (~60% down the content)
    // is placed right on the saddle line.
    const cb = contentBounds(ride);
    const contentFrac = Math.max(0.2, cb.bottom - cb.top);
    const seatFrac = 0.60;
    const desiredContentH = bullH * 0.98 * riderScale * (1 - 0.16 * duck);
    const drawH = desiredContentH / contentFrac;
    const scale = drawH / ride.naturalHeight;
    const w = ride.naturalWidth * scale;
    const rx = -w / 2 - bullW * 0.06;       // centered on the bull's back, nudged toward the spine
    const top = saddleY - (cb.top + seatFrac * contentFrac) * drawH - bullH * (0.12 + (char.rideDy || 0));
    const seatY = saddleY + bullH * 0.04;
    const bellyY = saddleY + bullH * 0.14;

    // 1) Upper body (head, torso, arms) — full width, down to the seat line.
    ctx.save();
    ctx.beginPath();
    ctx.rect(-w, top - 8, w * 2, seatY - (top - 8));
    ctx.clip();
    ctx.drawImage(ride, rx, top, w, drawH);
    ctx.restore();

    // 2) Lap/thighs — only a narrow central band over the bull's back, so legs
    //    straddle cleanly and never fan out sideways or hang below the belly.
    ctx.save();
    ctx.beginPath();
    ctx.rect(-bullW * 0.22, seatY, bullW * 0.44, bellyY - seatY);
    ctx.clip();
    ctx.drawImage(ride, rx, top, w, drawH);
    ctx.restore();

    // 3) Redraw the bull's lower body OVER the rider so the inside leg + feet
    //    tuck behind the bull — makes them straddle it, not sit pasted on top.
    if (ready(bull)) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(-bullW, saddleY + bullH * 0.08, bullW * 2, bullH * 1.6);
      ctx.clip();
      ctx.drawImage(bull, -bullW / 2, -bullH, bullW, bullH);
      ctx.restore();
    }
    ctx.restore();
    return { bullH, saddleY };
  }

  // Fallback (no ride sprite): clip a standing sprite just below the seat.
  const rimg = assets[baseId];
  const baseH = bullH * 0.92 * (char.spriteScale ? Math.min(1.12, char.spriteScale) : 1);
  const targetH = baseH * riderScale * (1 - 0.28 * duck);
  if (ready(rimg)) {
    const scale = targetH / rimg.naturalHeight;
    const w = rimg.naturalWidth * scale;
    const riderTop = saddleY - targetH * 0.5;
    const clipBottom = saddleY + bullH * 0.08;
    ctx.save();
    ctx.beginPath();
    ctx.rect(-w, riderTop - 6, w * 2, clipBottom - (riderTop - 6));
    ctx.clip();
    ctx.drawImage(rimg, -w / 2 - bullW * 0.01, riderTop, w, targetH);
    ctx.restore();
  } else {
    ctx.fillStyle = char.colors?.shirt || "#c33";
    ctx.fillRect(-bullW * 0.06, saddleY - bullH * 0.4, bullW * 0.12, bullH * 0.45);
    ctx.fillStyle = char.colors?.skin || "#e0b088";
    ctx.beginPath(); ctx.arc(0, saddleY - bullH * 0.44, bullW * 0.06, 0, Math.PI * 2); ctx.fill();
  }

  ctx.lineCap = "butt";
  ctx.restore();
  return { bullH, saddleY };
}
